/**
 * Embed configuration helpers (round9/embed-config) — the draft↔wire logic
 * behind EmbedPage.tsx, kept out of the .tsx so that file exports only
 * components (react-refresh/only-export-components) and so this logic is
 * unit-testable without mounting React. Same split as adminCeiling.ts /
 * CeilingFields.tsx.
 *
 * Policy note (CLAUDE.md Round 1 rule): this repo never re-derives
 * ENTITLEMENT logic from a tier. What it imports from @dockpro/core here is
 * catalog METADATA — tool labels/descriptions, option labels, the custom-bay
 * bounds — the same kind of import SettingsPage.tsx already makes for
 * MATERIAL_PRESETS. The one helper that touches entitlements
 * (`toolAllowedByScope`) reads the SERVER-RESOLVED `entitlements.editScope`
 * the tenant read already carries, never a tier name, and it only informs a
 * "not available under your current preset" note: the backend clamps the
 * stored toggles to the resolved scope on /api/config regardless.
 *
 * Wire semantics (mirror DockPro-Backend's portalUpdateTenantHandler.ts):
 *   toolOptions      — whole-field replace; this page always sends the FULL
 *                      9-key map so what the builder sees is what's stored.
 *   catalogOverrides — whole-field replace; a category with EVERY option
 *                      checked is OMITTED (so an unrestricted builder follows
 *                      future catalog growth automatically), and when no
 *                      category is restricted at all `null` is sent (clears).
 *                      A category with NOTHING checked is a client-side error
 *                      — the backend rejects it too (400).
 *   customBoatBays   — append-only server-side: existing keys must stay with
 *                      identical dimensions (label/short may change), so this
 *                      page only ever APPENDS new entries and edits labels.
 */
import {
  DESIGNER_TOOLS, DESIGNER_TOOL_KEYS, OPTION_CATEGORIES, OPTION_CATEGORY_KEYS,
 resolveCatalogOverrides } from "@dockpro/core";
import { CUSTOM_BAY_BOUNDS, CUSTOM_BAY_KEY_PATTERN, MAX_CUSTOM_BOAT_BAYS } from "@dockpro/core/catalogs";
import type {
  ToolOptions, CatalogOverrides, CustomBoatBay, DesignerToolKey, OptionCategoryKey,
} from "@dockpro/core/types";
import type { DesignerPresetName } from "./api";

export type OptionId = string | number;

// ---------------------------------------------------------------- presets
// Moved verbatim from SettingsPage.tsx (round7/d3-scope-config-portal) — the
// plain-language descriptions mirror api.ts's DesignerPresetName doc comment.
export const DESIGNER_PRESET_OPTIONS: Array<{ value: DesignerPresetName; label: string; description: string }> = [
  {
    value: "full",
    label: "Full designer",
    description: "Everything is available, including free-draw — homeowners " +
      "are never required to start from a template.",
  },
  {
    value: "guided",
    label: "Guided",
    description: "Every structural editing section is available, but a homeowner " +
      "must start from one of your templates (no blank-canvas start). They can " +
      "still edit structurally from there once started.",
  },
  {
    value: "showcase",
    label: "Showcase",
    description: "Look-only editing (materials, colors, and similar) — every " +
      "submission must match one of your approved templates exactly. This is " +
      "today's Basic-tier shape, now selectable independent of tier.",
  },
];

// ---------------------------------------------------------------- tools
export interface ToolMeta {
  key: DesignerToolKey;
  label: string;
  description: string;
  scope: string;
  scopeLabel: string;
}
export interface ToolGroup { scope: string; scopeLabel: string; tools: ToolMeta[] }

const TOOLS = DESIGNER_TOOLS as unknown as Record<DesignerToolKey, ToolMeta>;

/** DESIGNER_TOOLS grouped by their sidebar section, in registry order. */
export const TOOL_GROUPS: ToolGroup[] = (() => {
  const groups: ToolGroup[] = [];
  for (const key of DESIGNER_TOOL_KEYS) {
    const tool = TOOLS[key];
    let group = groups.find((g) => g.scope === tool.scope);
    if (!group) {
      group = { scope: tool.scope, scopeLabel: tool.scopeLabel, tools: [] };
      groups.push(group);
    }
    group.tools.push(tool);
  }
  return groups;
})();

export type ToolDraft = Record<DesignerToolKey, boolean>;

/** Missing key (or a never-configured tenant) = enabled; unknown keys dropped. */
export function toolDraftFromTenant(raw: ToolOptions | null | undefined): ToolDraft {
  const draft = {} as ToolDraft;
  for (const key of DESIGNER_TOOL_KEYS) draft[key] = raw?.[key] !== false;
  return draft;
}

/** The FULL 9-key map — whole-field replace on the wire. */
export function toolOptionsFromDraft(draft: ToolDraft): ToolOptions {
  const out: ToolOptions = {};
  for (const key of DESIGNER_TOOL_KEYS) out[key] = draft[key] !== false;
  return out;
}

/**
 * Does the tenant's SERVER-RESOLVED editScope grant this tool's section?
 * Undefined (a fixture without entitlements) reads as "full", matching the
 * embed's own absent-means-everything posture.
 */
export function toolAllowedByScope(editScope: string[] | "full" | undefined, scope: string): boolean {
  if (editScope === undefined || editScope === "full") return true;
  return Array.isArray(editScope) && editScope.includes(scope);
}

// ---------------------------------------------------------------- option categories
interface CategoryMeta {
  key: OptionCategoryKey;
  label: string;
  scope: string;
  structural: boolean;
  options: (customBoatBays?: unknown) => Array<{ key: OptionId; label: string }>;
}
const CATEGORIES = OPTION_CATEGORIES as unknown as Record<OptionCategoryKey, CategoryMeta>;

/** The variant choices inside each tool — "Design options" on the page. */
export const DESIGN_OPTION_CATEGORIES: OptionCategoryKey[] = [
  "enabledDockTypes", "enabledPilingShapes", "enabledBayStyles", "enabledGateLeaves",
  "enabledStairShapes", "enabledStairStyles", "enabledGangwayWidthsFt",
];
/** Catalog-backed finishes — "Materials & finishes" on the page. */
export const FINISH_CATEGORIES: OptionCategoryKey[] = [
  "enabledDeckMaterials", "enabledPilingStyles", "enabledRailColors", "enabledRoofStyles", "enabledRoofColors",
];
/** Stock + custom slip sizes — its own "Boat sizes" card on the page. */
export const BOAT_SIZE_CATEGORY: OptionCategoryKey = "enabledBoatBays";

export function categoryLabel(category: OptionCategoryKey): string {
  return CATEGORIES[category].label;
}

/** Every option a category can offer, in registry order (custom bays folded
 * into enabledBoatBays when passed). */
export function categoryOptions(
  category: OptionCategoryKey,
  customBoatBays?: CustomBoatBay[],
): Array<{ key: OptionId; label: string }> {
  return CATEGORIES[category].options(customBoatBays);
}

export type OptionDraft = Record<OptionCategoryKey, OptionId[]>;

/** Absent category = every option; stored ids not in the catalog are dropped. */
export function optionDraftFromTenant(
  raw: CatalogOverrides | null | undefined,
  customBoatBays?: CustomBoatBay[],
): OptionDraft {
  const draft = {} as OptionDraft;
  for (const category of OPTION_CATEGORY_KEYS) {
    const all = categoryOptions(category, customBoatBays).map((o) => o.key);
    const stored = raw?.[category];
    draft[category] = Array.isArray(stored) ? all.filter((k) => stored.includes(k)) : all;
  }
  return draft;
}

export type WireResult<T> = { ok: true; value: T } | { ok: false; error: string };

/**
 * Draft → wire. Omits a category when everything is checked; `null` when no
 * category is restricted at all (a clear). A category with nothing checked
 * is an error naming the category.
 */
export function catalogOverridesFromDraft(
  draft: OptionDraft,
  customBoatBays?: CustomBoatBay[],
): WireResult<CatalogOverrides | null> {
  const out: CatalogOverrides = {};
  let restricted = false;
  for (const category of OPTION_CATEGORY_KEYS) {
    const all = categoryOptions(category, customBoatBays).map((o) => o.key);
    const enabled = all.filter((k) => draft[category]?.includes(k));
    if (enabled.length === 0) {
      return {
        ok: false,
        error: `Keep at least one ${categoryLabel(category).toLowerCase()} option checked — ` +
          "an embed with none to pick would have nothing to offer.",
      };
    }
    if (enabled.length < all.length) {
      out[category] = enabled;
      restricted = true;
    }
  }
  return { ok: true, value: restricted ? out : null };
}

// ---------------------------------------------------------------- custom boat bays
interface Bounds { min: number; max: number }
export const CUSTOM_BAY_LIMITS = CUSTOM_BAY_BOUNDS as unknown as {
  beamFt: Bounds; lengthFt: Bounds; slipDepthFt: Bounds; labelMax: number; shortMax: number;
};
export const MAX_CUSTOM_BAYS = MAX_CUSTOM_BOAT_BAYS as number;

/** An unsaved row in the "Add custom size" editor (strings — they're inputs). */
export interface NewBayDraft {
  id: string;
  label: string;
  short: string;
  beamFt: string;
  lengthFt: string;
  slipDepthFt: string;
}

let newBaySeq = 0;
export function emptyNewBayDraft(): NewBayDraft {
  newBaySeq += 1;
  return { id: `new-${newBaySeq}`, label: "", short: "", beamFt: "", lengthFt: "", slipDepthFt: "" };
}

/**
 * Derive a stable, namespaced key from the label: `custom:<slug>`, unique
 * against `taken`, matching core's CUSTOM_BAY_KEY_PATTERN. Keys are
 * immutable once saved (see file header), so this runs exactly once per row.
 */
export function customBayKeyFor(label: string, taken: Set<string>): string {
  let slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
  if (!/^[a-z0-9]/.test(slug)) slug = slug ? `size-${slug}` : "size";
  slug = slug.replace(/-+$/g, "");
  let key = `custom:${slug}`;
  for (let n = 2; (taken.has(key) || !CUSTOM_BAY_KEY_PATTERN.test(key)) && n < 1000; n++) {
    key = `custom:${slug.slice(0, 30)}-${n}`;
  }
  return key;
}

function parseFeet(value: string): number | null {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isFinite(n) ? n : null;
}

/**
 * Stored rows (labels possibly edited) + new rows → the full list to send.
 * Client-side courtesy validation mirroring core's CUSTOM_BAY_BOUNDS; the
 * backend re-validates (400 INVALID_CUSTOM_BOAT_BAYS) and enforces the
 * append-only rule (422 CUSTOM_BOAT_BAY_IMMUTABLE).
 */
export function customBaysFromDrafts(
  stored: CustomBoatBay[],
  drafts: NewBayDraft[],
): WireResult<CustomBoatBay[]> {
  const B = CUSTOM_BAY_LIMITS;
  const out: CustomBoatBay[] = stored.map((b) => ({ ...b }));
  const taken = new Set(out.map((b) => b.key));
  for (const s of out) {
    const label = s.label.trim();
    if (!label) return { ok: false, error: "Every custom boat size needs a name." };
    if (label.length > B.labelMax) return { ok: false, error: `Boat size names must be ${B.labelMax} characters or fewer.` };
    s.label = label;
    if (s.short !== undefined) {
      const short = s.short.trim();
      if (short.length > B.shortMax) return { ok: false, error: `Short names must be ${B.shortMax} characters or fewer.` };
      if (short) s.short = short; else delete s.short;
    }
  }
  for (const d of drafts) {
    const label = d.label.trim();
    if (!label) return { ok: false, error: "Give each new boat size a name before saving." };
    if (label.length > B.labelMax) return { ok: false, error: `Boat size names must be ${B.labelMax} characters or fewer.` };
    const short = d.short.trim();
    if (short.length > B.shortMax) return { ok: false, error: `Short names must be ${B.shortMax} characters or fewer.` };
    const beamFt = parseFeet(d.beamFt);
    const lengthFt = parseFeet(d.lengthFt);
    const slipDepthFt = parseFeet(d.slipDepthFt);
    if (beamFt === null || beamFt < B.beamFt.min || beamFt > B.beamFt.max) {
      return { ok: false, error: `"${label}": beam must be a number between ${B.beamFt.min} and ${B.beamFt.max} ft.` };
    }
    if (lengthFt === null || lengthFt < B.lengthFt.min || lengthFt > B.lengthFt.max) {
      return { ok: false, error: `"${label}": length must be a number between ${B.lengthFt.min} and ${B.lengthFt.max} ft.` };
    }
    if (slipDepthFt === null || slipDepthFt < B.slipDepthFt.min || slipDepthFt > B.slipDepthFt.max) {
      return { ok: false, error: `"${label}": slip depth must be a number between ${B.slipDepthFt.min} and ${B.slipDepthFt.max} ft.` };
    }
    if (slipDepthFt < beamFt) {
      return { ok: false, error: `"${label}": slip depth must be at least the beam (${beamFt} ft).` };
    }
    const key = customBayKeyFor(label, taken);
    taken.add(key);
    out.push({ key, label, ...(short ? { short } : {}), beamFt, lengthFt, slipDepthFt });
  }
  if (out.length > MAX_CUSTOM_BAYS) {
    return { ok: false, error: `You can define up to ${MAX_CUSTOM_BAYS} custom boat sizes.` };
  }
  return { ok: true, value: out };
}

export function describeCustomBoatBay(b: CustomBoatBay): string {
  return `${b.label} — ${b.beamFt}′ beam × ${b.lengthFt}′ length, ${b.slipDepthFt}′ slip depth`;
}

// ---------------------------------------------------------------- rep summaries
export function describeToolOptions(
  raw: ToolOptions | null | undefined,
  editScope: string[] | "full" | undefined,
): string[] {
  const off = DESIGNER_TOOL_KEYS.filter((k) => raw?.[k] === false).map((k) => TOOLS[k].label);
  const unavailable = DESIGNER_TOOL_KEYS
    .filter((k) => !toolAllowedByScope(editScope, TOOLS[k].scope))
    .map((k) => TOOLS[k].label);
  const lines: string[] = [];
  lines.push(off.length ? `Turned off: ${off.join(", ")}.` : "Every tool is on.");
  if (unavailable.length) lines.push(`Not available under the current designer preset: ${unavailable.join(", ")}.`);
  return lines;
}

export function describeCatalogOverrides(
  raw: CatalogOverrides | null | undefined,
  customBoatBays?: CustomBoatBay[],
): string[] {
  // round10/debt-sweep (DEBT round9/embed-config #5): describe what the
  // embed will actually offer — the RESOLVED allow-lists, exactly as
  // /api/config serves them — rather than echoing the raw stored arrays. A
  // stored id that no longer exists in the catalog, or a category stored
  // with every option ticked, therefore reads the same way the homeowner
  // experiences it. Falls back to the raw description only if the stored
  // value cannot be resolved (the server validated it on write, so this is
  // a guard, not a path).
  let resolved: Partial<Record<OptionCategoryKey, OptionId[]>> | null;
  try {
    resolved = resolveCatalogOverrides(raw ?? undefined, { customBoatBays }) as Partial<Record<OptionCategoryKey, OptionId[]>>;
  } catch {
    resolved = null;
  }
  const lines: string[] = [];
  for (const category of OPTION_CATEGORY_KEYS) {
    const options = categoryOptions(category, customBoatBays);
    const allowed = resolved ? resolved[category] : raw?.[category];
    if (!Array.isArray(allowed)) continue;
    const offered = options.filter((o) => allowed.includes(o.key));
    if (offered.length >= options.length) continue; // nothing restricted here
    lines.push(`${categoryLabel(category)}: ${offered.map((o) => o.label).join(", ")}`);
  }
  return lines.length ? lines : ["Every option is offered."];
}

/** Backend error codes whose message is already homeowner/builder-safe and
 * specific — surfaced verbatim by EmbedPage.tsx, same convention
 * SettingsPage.tsx established for the preset codes. */
export const EMBED_CONFIG_ERROR_CODES = new Set([
  "INVALID_DESIGNER_PRESET", "PRESET_NOT_ALLOWED_FOR_TIER",
  "INVALID_TOOL_OPTIONS", "INVALID_CATALOG_OVERRIDES",
  "INVALID_CUSTOM_BOAT_BAYS", "CUSTOM_BOAT_BAY_IMMUTABLE",
]);
